import { useMemo } from "react";

import { usePostHogRuntime } from "./PostHogProvider";

/**
 * Read a PostHog boolean feature flag with a required default.
 *
 * Behaviour:
 * - When PostHog is not configured (no `VITE_POSTHOG_KEY`), always returns `defaultValue`.
 * - While PostHog is still loading flags, returns `defaultValue`.
 * - Once flags are available, returns the evaluated value (coerced to boolean).
 *
 * Re-renders automatically when PostHog's flag set changes.
 */
export function useFeatureFlag(key: string, defaultValue: boolean): boolean {
  const { enabled, client, flagsRevision } = usePostHogRuntime();

  return useMemo(() => {
    if (!enabled) return defaultValue;
    const value = client.isFeatureEnabled(key);
    // isFeatureEnabled returns `undefined` until the first flag payload arrives.
    if (value === undefined) return defaultValue;
    return Boolean(value);
    // flagsRevision is intentionally in deps so the value refreshes on flag updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, client, key, defaultValue, flagsRevision]);
}

/**
 * Read a multivariate (string) feature flag with a required default.
 */
export function useFeatureFlagVariant(key: string, defaultValue: string): string {
  const { enabled, client, flagsRevision } = usePostHogRuntime();

  return useMemo(() => {
    if (!enabled) return defaultValue;
    const value = client.getFeatureFlag(key);
    if (value === undefined || value === null) return defaultValue;
    return String(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, client, key, defaultValue, flagsRevision]);
}
